// src/services/planService.js
import { doc, getDoc, updateDoc, collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from './firebase';
import { getPlanConfig, canAddUser, isModuleInPlan } from '../config/plans';

/**
 * Obtener el plan actual de un workspace
 */
export const getWorkspacePlan = async (workspaceId) => {
  const snap = await getDoc(doc(db, 'workspaces', workspaceId));
  if (!snap.exists()) return 'free';
  return snap.data().plan || 'free';
};

/**
 * Contar usuarios actuales de un workspace
 */
export const getWorkspaceUserCount = async (workspaceId) => {
  // Contar owner + colaboradores
  const q = query(collection(db, 'users'), where('workspaceId', '==', workspaceId));
  const snap = await getCountFromServer(q);
  return snap.data().count;
};

/**
 * Verificar si se puede agregar un usuario al workspace
 * Retorna { allowed: boolean, reason?: string, remaining?: number }
 */
export const checkCanAddUser = async (workspaceId) => {
  // Super admin has unlimited users - check by ownerEmail or by user doc email
  const SUPER_ADMIN_EMAIL = 'vcecursos@gmail.com';
  try {
    const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
    const ownerEmail = wsSnap.exists() ? wsSnap.data().ownerEmail : null;
    if (ownerEmail === SUPER_ADMIN_EMAIL) {
      return { allowed: true, remaining: Infinity, currentCount: 0, plan: getPlanConfig('enterprise') };
    }
    // Also check if the workspaceId user doc has the super admin email
    const userSnap = await getDoc(doc(db, 'users', workspaceId));
    if (userSnap.exists() && userSnap.data().email === SUPER_ADMIN_EMAIL) {
      return { allowed: true, remaining: Infinity, currentCount: 0, plan: getPlanConfig('enterprise') };
    }
  } catch { /* continue with normal check */ }

  const plan = await getWorkspacePlan(workspaceId);
  const config = getPlanConfig(plan);
  const currentCount = await getWorkspaceUserCount(workspaceId);

  if (canAddUser(plan, currentCount)) {
    const remaining = config.maxUsers === Infinity ? Infinity : config.maxUsers - currentCount;
    return { allowed: true, remaining, currentCount, plan: config };
  }

  return {
    allowed: false,
    reason: `Tu plan ${config.name} permite maximo ${config.maxUsers} usuarios. Actualmente tienes ${currentCount}. Actualiza a un plan superior.`,
    currentCount,
    plan: config,
  };
};

/**
 * Verificar si un modulo esta disponible para el workspace
 * Retorna { allowed: boolean, reason?: string }
 */
export const checkModuleAccess = async (workspaceId, moduleKey) => {
  const plan = await getWorkspacePlan(workspaceId);
  const config = getPlanConfig(plan);

  if (isModuleInPlan(plan, moduleKey)) {
    return { allowed: true, plan: config };
  }

  return {
    allowed: false,
    reason: `El modulo no esta incluido en tu plan ${config.name}. Actualiza a Pro o Enterprise para acceder.`,
    plan: config,
  };
};

/**
 * Cambiar el plan de un workspace (super-admin)
 */
export const changeWorkspacePlan = async (workspaceId, newPlan) => {
  const config = getPlanConfig(newPlan);

  // Actualizar workspace
  await updateDoc(doc(db, 'workspaces', workspaceId), {
    plan: newPlan,
    planUpdatedAt: new Date(),
  });

  // Actualizar modulos del workspace segun el plan
  const modules = {};
  config.modules.forEach(m => { modules[m] = true; });

  // Desactivar modulos que no estan en el plan
  const allModuleKeys = ['gestorPro', 'generador', 'tareas', 'calendario', 'agenteIA', 'horasExtra', 'ventas'];
  allModuleKeys.forEach(m => {
    if (!modules[m]) modules[m] = false;
  });

  await updateDoc(doc(db, 'workspaces', workspaceId), { modules });

  // Actualizar appPermissions del owner
  await updateDoc(doc(db, 'users', workspaceId), { appPermissions: modules });

  return { plan: newPlan, modules };
};

/**
 * Obtener resumen de uso del workspace vs limites del plan
 */
export const getWorkspaceUsage = async (workspaceId) => {
  const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
  if (!wsSnap.exists()) return null;

  const wsData = wsSnap.data();
  const plan = getPlanConfig(wsData.plan || 'free');
  const userCount = await getWorkspaceUserCount(workspaceId);

  return {
    plan: wsData.plan || 'free',
    planConfig: plan,
    users: {
      current: userCount,
      max: plan.maxUsers,
      percentage: plan.maxUsers === Infinity ? 0 : Math.round((userCount / plan.maxUsers) * 100),
    },
    modules: {
      available: plan.modules,
      total: 8,
      active: plan.modules.length,
    },
  };
};
